

import React from 'react';

import { MenuItem, Select, Stack, TextField, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles';

import { MiniGroupController, MiniImage } from '../mini/MiniView';
import { creatureVariants, maxHeightRatio, effectiveRenderStyle } from '../../utils/util';
import { Box } from '@mui/system';

import { CirclePlus, Trash2, CircleMinus, FlipHorizontal2 } from 'lucide-react';
import { useEffect } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DEFAULT_PAPER, PAGE_MARGIN } from './paperSizes';
import { SPILL_HEADER } from './MyPack';

const useStyles = makeStyles((theme) => ({
    root: {
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
    },
    canvas: {
        border: "1px black",
        borderStyle: "dashed",
        width: "100%",

    },
    hidden: {
        display: "none"
    }

}));

/**
 * Check whether the event occurred roughly inside (or above) the element.
 * 
 * @param {MouseEvent} event Event to check.
 * @param {Node} element Element to check.
 */
function isEventInElement(event, element) {
    var rect = element.getBoundingClientRect();
    var x = event.clientX;
    if (x < rect.left || x >= rect.right) return false;
    var y = event.clientY;
    if (y < rect.top || y >= rect.bottom) return false;
    return true;
}

function NestedSVG(props) {
    const { id, width, height, x, y, ...eventHandlers } = props;
    return (
        <svg id={id} x={x} y={y} width={width} height={height} {...eventHandlers} >
            {props.children}
        </svg>
    )
}

/**
 * Draws an SVG canvas with the resolution scaled to the the DPI of a page, but with 
 * a viewbox that allows all children coordinates to be given in Inches
 * @param {dpi} DPI of the page 
 * @param {format} { pageWith, pageHeight } in inches
 * @returns 
 */
// `viewSize` is the full drawable area in inches — normally the page, but taller
// than the page when overflow groups spill into the band below it, so those
// groups stay visible instead of being clipped at the page edge.
function PaperCanvas(props) {
    const { dpi, viewSize, children, onDrop, onDragOver, onMouseLeave } = props;
    return (
        <svg viewBox={`0 0 ${viewSize.width * dpi} ${viewSize.height * dpi}`} onDrop={onDrop} onDragOver={onDragOver} onMouseLeave={onMouseLeave} >
            <filter id="blur">
                <feGaussianBlur stdDeviation="0.1" />
            </filter>
            <svg viewBox={`0 0 ${viewSize.width} ${viewSize.height}`} x="0" y="0" width="100%" height="100%" >
                {children}
            </svg>
        </svg>
    )
}


const NUMBERING_OPTIONS = [
    { value: "none", label: "-" },
    { value: "numeric", label: "1,2,3..." },
    { value: "alpha", label: "A,B,C..." },
    { value: "roman", label: "I,II,III..." },
];

const STYLE_OPTIONS = [
    { value: "standee", label: "Standee" },
    { value: "rectToken", label: "Rectangular token" },
    { value: "roundToken", label: "Round token" },
];

const SIZE_OPTIONS = [
    { value: 0.5, label: "Tiny" },
    { value: 1, label: "Medium/Small" },
    { value: 2, label: "Large" },
    { value: 3, label: "Huge" },
    { value: 4, label: "Gargantuan" },
];

const groupIconProps = { size: 20, strokeWidth: 2, style: { cursor: 'pointer' } };

// The GroupGUI panel is a frosted-glass overlay floating over arbitrary (often
// dark/busy) mini artwork, so it stays a fixed light surface regardless of the
// app's light/dark theme. Its ink/border colors are pinned rather than pulled
// from the theme-relative --text-*/--border-* vars, which would go pale-on-pale
// in dark mode once the panel itself stops following the theme.
const PANEL_INK = '#33291F'; // sand-900
const PANEL_BORDER = 'rgba(51, 41, 31, 0.16)';
const PANEL_BORDER_STRONG = 'rgba(51, 41, 31, 0.28)';

const groupSelectSx = {
    fontSize: '0.75rem',
    color: PANEL_INK,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: PANEL_BORDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PANEL_BORDER_STRONG },
    '& .MuiSelect-select': { paddingTop: '4px', paddingBottom: '4px' },
};

const groupCountFieldSx = {
    width: '2.5em',
    '& .MuiInputBase-input': {
        color: PANEL_INK,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        textAlign: 'center',
    },
    '& .MuiInput-underline:before': { borderBottomColor: PANEL_BORDER },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: PANEL_BORDER_STRONG },
    '& .MuiInput-underline:after': { borderBottomColor: 'var(--color-primary)' },
};

function GroupGUI(props) {
    const { group, x, y } = props;
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
    const [countInput, setCountInput] = React.useState(String(group.count ?? 1));
    const [startInput, setStartInput] = React.useState(String(group.startNumber ?? 1));
    const creatureName = group.creature ? group.creature.name : 'this mini';
    const effectiveSize = group.size ? group.size : group.creature.size;
    const numbering = group.numbering ? group.numbering : "numeric";
    const renderStyle = effectiveRenderStyle(group);
    const isToken = renderStyle !== "standee";

    useEffect(() => {
        setCountInput(String(group.count));
    }, [group.id, group.count]);

    useEffect(() => {
        setStartInput(String(group.startNumber ?? 1));
    }, [group.id, group.startNumber]);

    return (
        <MiniGroupController group={group} >
            {controller => {
                const onDecrement = () => controller.doUpdate({ count: group.count > 1 ? group.count - 1 : group.count });
                const onIncrement = () => controller.doUpdate({ count: group.count + 1 });
                const onFlip = () => controller.doUpdate({ vertical: !group.vertical });
                const onNumberingChange = e => controller.doUpdate({ numbering: e.target.value });
                const onSizeChange = e => controller.doUpdate({ size: e.target.value });
                const onStyleChange = e => controller.doUpdate({ renderStyle: e.target.value });
                const onDeleteConfirm = () => { setConfirmDeleteOpen(false); controller.doDelete(); };
                const onCountInputChange = e => setCountInput(e.target.value);
                const commitCountInput = () => {
                    const parsed = parseInt(countInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= 1) {
                        if (parsed !== group.count) controller.doUpdate({ count: parsed });
                        setCountInput(String(parsed));
                    } else {
                        setCountInput(String(group.count));
                    }
                };
                const onCountInputKeyDown = e => { if (e.key === 'Enter') e.currentTarget.blur(); };
                const onStartInputChange = e => setStartInput(e.target.value);
                const commitStartInput = () => {
                    const parsed = parseInt(startInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= 1) {
                        if (parsed !== (group.startNumber ?? 1)) controller.doUpdate({ startNumber: parsed });
                        setStartInput(String(parsed));
                    } else {
                        setStartInput(String(group.startNumber ?? 1));
                    }
                };

                return (
                <Box sx={{
                    background: 'rgba(255, 255, 255, 0.72)',
                    backdropFilter: 'blur(10px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
                    position: "absolute",
                    left: x, top: y,
                    padding: '0.3em',
                    borderRadius: "1em",
                    transform: "translate(-50%, -50%)",
                    boxShadow: 'var(--shadow-md)',
                    '@media print': { display: 'none' }
                }}>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Stack spacing={1} direction="row" justifyContent="center" alignItems="center">
                            <Tooltip title="Remove one">
                                <CircleMinus color="var(--color-primary)" {...groupIconProps} onClick={onDecrement} />
                            </Tooltip>
                            <TextField
                                variant="standard"
                                size="small"
                                value={countInput}
                                onChange={onCountInputChange}
                                onBlur={commitCountInput}
                                onKeyDown={onCountInputKeyDown}
                                onFocus={e => e.target.select()}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                sx={groupCountFieldSx}
                            />
                            <Tooltip title="Add one">
                                <CirclePlus color="var(--color-primary)" {...groupIconProps} onClick={onIncrement} />
                            </Tooltip>
                        </Stack>
                        <Stack spacing={1} direction="row">
                            {!isToken && (
                                <Tooltip title="Flip folding direction">
                                    <FlipHorizontal2 color="var(--color-primary)" {...groupIconProps} onClick={onFlip} />
                                </Tooltip>
                            )}
                            <Tooltip title="Delete group">
                                <Trash2 color="var(--color-danger)" {...groupIconProps} onClick={() => setConfirmDeleteOpen(true)} />
                            </Tooltip>
                            <ConfirmDialog
                                open={confirmDeleteOpen}
                                title={`Remove this group of ${creatureName} from the sheet?`}
                                description="This can't be undone."
                                confirmLabel="Remove"
                                onCancel={() => setConfirmDeleteOpen(false)}
                                onConfirm={onDeleteConfirm}
                            />

                        </Stack>
                        <Stack spacing={0.5}>
                            <Select sx={groupSelectSx}
                                variant="outlined"
                                aria-labelledby="render-style-label"
                                name="render-style-group"
                                size="small"
                                label="Style"
                                value={renderStyle}
                                onChange={onStyleChange}
                            >
                                {STYLE_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                            {!isToken && (
                                <Select sx={groupSelectSx}
                                    variant="outlined"
                                    aria-labelledby="type-of-numbering-label"
                                    name="type-of-numbering-group"
                                    size="small"
                                    label="Numbering"
                                    value={numbering}
                                    onChange={onNumberingChange}
                                >
                                    {NUMBERING_OPTIONS.map(({ value, label }) => (
                                        <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                </Select>
                            )}
                            <Select sx={groupSelectSx}
                                variant="outlined"
                                aria-labelledby="size-label"
                                name="size-group"
                                size="small"
                                label="Size"
                                value={effectiveSize}
                                onChange={onSizeChange}
                            >
                                {SIZE_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                            {!isToken && numbering !== "none" && (
                                <Tooltip title="Start numbering from">
                                    <TextField
                                        variant="standard"
                                        size="small"
                                        label="Start at"
                                        value={startInput}
                                        onChange={onStartInputChange}
                                        onBlur={commitStartInput}
                                        onKeyDown={onCountInputKeyDown}
                                        onFocus={e => e.target.select()}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        InputLabelProps={{ sx: { fontSize: '0.7rem', color: PANEL_INK } }}
                                        sx={[groupCountFieldSx, { width: '100%' }]}
                                    />
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                </Box>
                );
            }}
        </MiniGroupController>
    );
}

function romanize(num) {
    var lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 }, roman = '', i;
    for (i in lookup) {
        while (num >= lookup[i]) {
            roman += i;
            num -= lookup[i];
        }
    }
    return roman;
}

function toNumberingLabel(type, number) {
    switch (type) {
        case "alpha":
            return (number + 9).toString(36).toUpperCase();
        case "roman":
            return romanize(number);
        case "numeric":
            return number.toString();
        case "none":
            return '';
        default:
            return '';
    }
}
export const SheetCanvas = React.forwardRef((props, ref) => {

    const { groups, onDrop, showGui, paper } = props;
    const classes = useStyles();
    const [active, setActive] = React.useState(null)

    // console.log("groups", groups)
    console.log("todo: make canvas DPI a prop, and accept pagesize as prop")
    const dpi = 300;
    const effectivePaper = paper || DEFAULT_PAPER;
    // Same margin the packer reserves (PackedSheetView), so what packs is exactly
    // what draws — no bottom/right clipping (MINI-28).
    const paperSize = { width: effectivePaper.width - PAGE_MARGIN, height: effectivePaper.height - PAGE_MARGIN };

    // Groups the packer couldn't fit on the page (MINI-29). They still carry
    // valid, non-overlapping coordinates in the spill band below the page, so we
    // grow the drawable area to show them rather than clipping at the page edge.
    const overflowGroups = groups.filter(g => g.overflow);
    const hasOverflow = overflowGroups.length > 0;
    const contentBottom = groups.reduce(
        (m, g) => Math.max(m, (g.y || 0) + (g.height || 0)), paperSize.height);
    const SPILL_PAD = 0.3; // inches of breathing room under the last spilled row
    const viewSize = {
        width: paperSize.width,
        height: hasOverflow ? contentBottom + SPILL_PAD : paperSize.height,
    };

    useEffect(() => {
        // Only clear the active selection if the active group was actually
        // removed. Editing the active group (count/size/flip/numbering)
        // changes the `groups` reference too, but shouldn't tear down its GUI.
        if (active != null && !groups.some(g => g.id === active)) {
            clearSelection();
        }
    }, [groups, active])

    function onDragOver(e) {
        e.preventDefault();
    }

    const clearSelection = () => {
        setActive(null);
    }

    // Freeze the GUI's position to where the group was centered when it became
    // active. Only recomputed when a *different* group is hovered — not on every
    // edit to the active group — so the toolbar doesn't chase count/size changes.
    const guiAnchor = React.useMemo(() => {
        const group = groups.find(g => g.id === active);
        if (!group) return null;
        // Percentages are relative to the full drawable area (viewSize), which is
        // taller than the page when groups spill below it — so a spilled group's
        // toolbar still anchors over the group, not off the top.
        return {
            x: `${100 * (group.x + group.width / 2) / viewSize.width}%`,
            y: `${100 * (group.y + group.height / 2) / viewSize.height}%`,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const guis = guiAnchor ? groups.filter(g => g.id === active).map(group =>
        <GroupGUI
            group={group} id={`gui-${group.id}`} key={`gui-${group.id}`}
            x={guiAnchor.x}
            y={guiAnchor.y}
        />
    ) : [];


    var miniImageList = groups.map(group => {
        const creature = group.creature;
        // console.log("Group", group)

        let minis = [];
        const size = group.size ? group.size : group.creature.size;
        // Rotate through the creature's image variants so a group shows variety.
        // Every mini uses the tallest variant's ratio so they share a base line;
        // shorter art is bottom-anchored within that box (see MiniImage).
        const variants = creatureVariants(creature);
        const groupRatio = maxHeightRatio(creature);
        const renderStyle = effectiveRenderStyle(group);
        const isToken = renderStyle !== 'standee';
        const outlineRect = (<rect id={"outline-rect" + group.id} className="noprint print-hide-stroke" width={group.width} height={group.height} x={group.x} y={group.y} fill={"none"} stroke={"blue"} strokeWidth={size * 0.01} fillOpacity={0.8} />)

        // A group may be wrapped into a grid (MINI-26). columns/rows come from
        // toSize; older callers without them fall back to a single row.
        const columns = group.columns || group.count;
        const rows = group.rows || 1;
        const cellWidth = group.width / columns;
        const cellHeight = group.height / rows;

        for (let i = 0; i < group.count; i++) {
            const id = creature.id + "-" + i;

            const col = i % columns;
            const row = Math.floor(i / columns);

            const startNumber = group.startNumber ?? 1;
            // Tokens have no base to hold a number, so they carry no label (MINI-24).
            let label = isToken ? '' : toNumberingLabel(group.numbering || "numeric", i + startNumber);

            // Cycle the image src; pin every mini to the group's tallest ratio
            // so the box (and fold/base line) is uniform across the group.
            const variant = variants.length > 0 ? variants[i % variants.length] : { src: creature.src };
            const variantCreature = { ...creature, src: variant.src, heightRatio: groupRatio };

            minis.push(
                <NestedSVG key={id}
                    onMouseOver={e => { setActive(group.id) }}
                    x={group.x + col * cellWidth} y={group.y + row * cellHeight}
                    width={cellWidth}
                    height={cellHeight}>
                    <MiniImage sizeOverride={size} creature={variantCreature} vertical={group.vertical} imageId={group.id + "_" + i} label={label} renderStyle={renderStyle} />
                </NestedSVG>);
        }

        return (
            <g id={`group-${group.id}`} key={`group-${group.id}`}  >
                {showGui && active === group.id ? outlineRect : ""}
                {minis}
            </g>);
    });

    if (miniImageList.length === 0) {

        miniImageList = (
            <svg viewBox="0 0 640 280">
                <text x={40} y={120} fontSize="2em" fill="grey">Drag a mini here to add it to this sheet.</text>
                <text x={40} y={150} fill="grey">From your library, or any image from a web page.</text>
            </svg>
        )
    }

    // TODO: all users are treated as non-premium for now. Once accounts have a
    // premium flag, only render this for non-premium users.
    const blockedWidth = paperSize.width / 3;
    const blockedX = paperSize.width - blockedWidth;
    const watermark = (
        <g id="premium-watermark">
            <line x1={blockedX} y1={0} x2={blockedX} y2={paperSize.height}
                stroke="rgba(120,120,120,0.5)" strokeWidth={0.02} strokeDasharray="0.08,0.08" />
            <image href={`${process.env.PUBLIC_URL}/Beggar-by-gemini.jpg`}
                x={blockedX} y={0} width={blockedWidth} height={paperSize.height}
                preserveAspectRatio="xMidYMid meet" opacity={0.5} />
            <text x={blockedX + blockedWidth / 2} y={paperSize.height - 0.45} fontSize={0.22}
                fill="rgba(90,90,90,0.6)" textAnchor="middle" fontFamily="sans-serif">Free tier.</text>
            <text x={blockedX + blockedWidth / 2} y={paperSize.height - 0.15} fontSize={0.22}
                fill="rgba(90,90,90,0.6)" textAnchor="middle" fontFamily="sans-serif">Premium users get more!</text>
        </g>
    );


    // The spill band: everything below this line is off the page. It's drawn
    // behind the spilled minis so they stay legible and interactive — the user
    // can shrink or remove them in place until the sheet fits again (MINI-29).
    const overflowBand = hasOverflow ? (
        <g id="overflow-band" className="noprint">
            <rect x={0} y={paperSize.height} width={paperSize.width} height={viewSize.height - paperSize.height}
                fill="rgba(200,60,40,0.06)" />
            <line x1={0} y1={paperSize.height} x2={paperSize.width} y2={paperSize.height}
                stroke="var(--color-danger)" strokeWidth={0.02} strokeDasharray="0.1,0.06" />
            <text x={paperSize.width / 2} y={paperSize.height + SPILL_HEADER / 2} fontSize={0.2}
                fill="var(--color-danger)" textAnchor="middle" dominantBaseline="middle" fontFamily="sans-serif">
                Doesn't fit on the page — resize or remove these
            </text>
        </g>
    ) : null;

    return (
        <div style={{ position: "relative" }} >
            <div ref={ref} >
                <PaperCanvas dpi={dpi} viewSize={viewSize}
                    onMouseLeave={e => { if (!isEventInElement(e, e.currentTarget)) clearSelection(); }}
                    onDragOver={onDragOver}
                    onDrop={e => onDrop(e)} >
                    <svg className={classes.canvas}
                        onMouseLeave={e => {
                            if (!isEventInElement(e, e.currentTarget)) clearSelection(); // ignore mouseleave when within canvas
                        }}
                    >
                        {overflowBand}
                        {miniImageList}
                        {watermark}
                    </svg>
                </PaperCanvas>
            </div>
            {showGui && active != null ? guis : ""}
        </div>

    )
});
